import {
	ConfidenceLevel,
	DataRequestStatus,
	EmissionValueGranularityLevel,
} from '@api-client/api'

import type { IAppliedFilters } from './types'

const castNullToUndefined = <T>(
	value?: T | null
): undefined | NonNullable<T> => {
	if (value === null) {
		return undefined
	}
	return value
}

const maybeBundleFromTo = <T>(
	from?: T,
	to?: T
): undefined | { from?: NonNullable<T>; to?: NonNullable<T> } => {
	const fromValue = castNullToUndefined(from)
	const toValue = castNullToUndefined(to)
	if (fromValue === undefined && toValue === undefined) {
		return undefined
	}
	return { from: fromValue, to: toValue }
}

const maybeParseFloat = (value?: string | null): undefined | number => {
	if (value === undefined || value === null) {
		return undefined
	}
	const valueAsNumber = Number.parseFloat(value)
	if (Number.isNaN(valueAsNumber)) {
		return undefined
	}
	return valueAsNumber
}

const filterEmptyArray = <T>(value: readonly T[]): undefined | readonly T[] =>
	value.length > 0 ? value : undefined

const emissionDataRequestStatusValues = new Set(
	Object.values(DataRequestStatus)
)
const draftEmissionValuesEmissionValuesGranularityValues = new Set(
	Object.values(EmissionValueGranularityLevel)
)
const draftEmissionValuesConfidenceValues = new Set(
	Object.values(ConfidenceLevel)
)

export const parseFiltersFromSearchString = (
	search: string
): IAppliedFilters => {
	const params = new URLSearchParams(search)
	return {
		lastUpdated: maybeBundleFromTo(
			params.get('lastUpdatedFrom'),
			params.get('lastUpdatedTo')
		),
		productRequestStatus: filterEmptyArray(
			params
				.getAll('productRequestStatus')
				.flatMap((s: string) => s.split(','))
				.filter((s: string): s is DataRequestStatus =>
					emissionDataRequestStatusValues.has(s as DataRequestStatus)
				)
		),
		emissionValueGranularity: filterEmptyArray(
			params
				.getAll('emissionValueGranularity')
				.flatMap((g: string) => g.split(','))
				.filter((g: string): g is EmissionValueGranularityLevel =>
					draftEmissionValuesEmissionValuesGranularityValues.has(
						g as EmissionValueGranularityLevel
					)
				)
		),
		emissionDataVerified: filterEmptyArray(
			params
				.getAll('emissionDataVerified')
				.flatMap((v: string) => v.split(','))
				.map((v: string) => v === 'true' || v === '1')
		),
		emissionValue: maybeBundleFromTo(
			maybeParseFloat(params.get('emissionValueFrom')),
			maybeParseFloat(params.get('emissionValueTo'))
		),
		emissionValueConfidence: filterEmptyArray(
			params
				.getAll('emissionValueConfidence')
				.flatMap((c: string) => c.split(','))
				.filter((c: string): c is ConfidenceLevel =>
					draftEmissionValuesConfidenceValues.has(c as ConfidenceLevel)
				)
		),
		shareOfPrimaryDataInEmissionFactor: maybeBundleFromTo(
			maybeParseFloat(params.get('shareOfPrimaryDataInEmissionFactorFrom')),
			maybeParseFloat(params.get('shareOfPrimaryDataInEmissionFactorTo'))
		),
		selectedSuppliers: filterEmptyArray(params.getAll('selectedSuppliers')),
		selectedCustomers: filterEmptyArray(params.getAll('selectedCustomers')),
	}
}

export const parseSortingFromSearchString = <T>(search: string) => {
	const params = new URLSearchParams(search)
	return {
		sort: (params.get('sort') as T) || undefined,
		order: (params.get('order') as 'ASC' | 'DESC') || undefined,
	}
}

interface FromTo {
	readonly from?: unknown
	readonly to?: unknown
}

const isFromTo = <T>(value: T): value is FromTo & T =>
	(value as FromTo)?.from !== undefined || (value as FromTo)?.to !== undefined

// All possible filter params.
// Exported only for testing.
export const FILTER_PARAMS = [
	'emissionDataVerified',
	'emissionValueConfidence',
	'emissionValueFrom',
	'emissionValueGranularity',
	'emissionValueTo',
	'lastUpdatedFrom',
	'lastUpdatedTo',
	'productRequestStatus',
	'selectedCustomers',
	'selectedSuppliers',
	'shareOfPrimaryDataInEmissionFactorFrom',
	'shareOfPrimaryDataInEmissionFactorTo',
] as const

export const createSearchStringFromFilters = (
	search: string,
	filters: IAppliedFilters
) => {
	const params = new URLSearchParams(search)
	for (const filter of FILTER_PARAMS) {
		params.delete(filter)
	}
	for (const [key, value] of Object.entries(filters)) {
		if (value === undefined) {
			continue
		}

		if (isFromTo(value)) {
			if (value.from !== undefined && value.from !== null) {
				params.set(`${key}From`, value.from.toString())
			}
			if (value.to !== undefined && value.to !== null) {
				params.set(`${key}To`, value.to.toString())
			}
			continue
		}

		params.set(key, value)
	}
	params.sort()
	return params.toString()
}
